import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { useWebSocket } from '../context/WebSocketContext.js';
import {
  Printer,
  Radio,
  Bell,
  BellOff,
  LogOut,
  AlertTriangle,
  ShieldAlert,
  ShieldCheck,
  Plus,
  Sparkles,
  Settings,
  ChevronDown,
  User,
  LayoutDashboard,
} from 'lucide-react';

export const Navbar: React.FC<{
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  onOpenSimulateModal?: () => void;
  currentPath?: string;
  navigate?: (path: string) => void;
}> = ({ activeTab, setActiveTab, onOpenSimulateModal, currentPath = window.location.pathname, navigate }) => {
  const { user, shop, logout } = useAuth();
  const { agentOnline, isConnected, audioAlertEnabled, setAudioAlertEnabled, currentAdminShopId } = useWebSocket();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Calculate days remaining in subscription
  let daysLeft: number | null = null;
  if (shop?.valid_until) {
    const diff = new Date(shop.valid_until).getTime() - Date.now();
    daysLeft = Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  const portalLabel =
    currentPath === '/superadmin' ? 'Super Admin' :
    currentPath === '/admin' ? 'Admin Portal' :
    'Shop Terminal';

  const portalColor =
    currentPath === '/superadmin' ? 'text-purple-400' :
    currentPath === '/admin' ? 'text-indigo-400' :
    'text-emerald-400';

  return (
    <header className="glass-nav sticky top-0 z-40">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-0 flex items-center justify-between h-14 gap-4">
        
        {/* ── Brand ── */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 flex-shrink-0">
            <Printer className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-white tracking-tight whitespace-nowrap">PrintBridge</span>
              <span className="hidden sm:inline text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono font-bold">
                v1.0
              </span>
            </div>
            <div className="hidden lg:flex items-center gap-2 text-[11px] font-mono">
              {currentPath === '/admin' || currentPath === '/superadmin' ? (
                <span className="flex items-center gap-1.5 text-slate-400">
                  <span className="pulse-dot">
                    <span className="pulse-dot-inner bg-emerald-400" />
                  </span>
                  <span className="text-emerald-400 font-medium">Platform Online</span>
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-slate-400">
                  <span className="pulse-dot">
                    <span className={`pulse-dot-inner ${agentOnline ? 'bg-emerald-400' : 'bg-rose-500'}`} />
                  </span>
                  <span className={agentOnline ? 'text-emerald-400' : 'text-rose-400'}>
                    {agentOnline ? 'Agent Online' : 'Agent Offline'}
                  </span>
                </span>
              )}
              <span className="text-slate-700">·</span>
              <span className={`font-medium ${portalColor}`}>{portalLabel}</span>
            </div>
          </div>
        </div>

        {/* ── Right Controls ── */}
        <div className="flex items-center gap-2">
          {/* WS Status */}
          <div title={isConnected ? 'Live WebSocket Connected' : 'Reconnecting...'}>
            <Radio className={`w-3.5 h-3.5 ${isConnected ? 'text-emerald-400' : 'text-slate-600 animate-spin'}`} />
          </div>

          {/* Subscription Warning */}
          {daysLeft !== null && daysLeft <= 7 && daysLeft > 0 && (
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[11px] font-semibold">
              <AlertTriangle className="w-3 h-3" />
              <span>{daysLeft}d left</span>
            </div>
          )}

          {/* Grace Period */}
          {shop?.status === 'GRACE' && (
            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-400 text-[11px] font-semibold animate-pulse">
              <ShieldAlert className="w-3 h-3" />
              <span>Grace</span>
            </div>
          )}



          {/* Superadmin cross-portal */}
          {user?.role === 'superadmin' && navigate && (
            <button
              onClick={() => navigate(currentPath === '/superadmin' ? '/admin' : '/superadmin')}
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/20 text-indigo-300 text-[12px] font-semibold transition-all"
              title="Switch Portal"
            >
              <LayoutDashboard className="w-3.5 h-3.5" />
              <span>{currentPath === '/superadmin' ? '/admin' : '/superadmin'}</span>
            </button>
          )}

          {/* Audio toggle */}
          <button
            onClick={() => setAudioAlertEnabled(!audioAlertEnabled)}
            title={audioAlertEnabled ? 'Mute order chime' : 'Enable order chime'}
            className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-all ${
              audioAlertEnabled
                ? 'bg-white/5 border-white/08 text-slate-400 hover:text-white'
                : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
            }`}
          >
            {audioAlertEnabled ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
          </button>

          {/* User menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 pl-2 pr-1.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/08 transition-all"
            >
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <User className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="hidden sm:block text-[12px] font-semibold text-slate-300 max-w-[80px] truncate">
                {user?.username || user?.email || 'User'}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 glass-modal rounded-xl overflow-hidden animate-scale-in z-50">
                <div className="px-3 py-2.5 border-b border-white/07">
                  <div className="text-[11px] text-slate-400">Signed in as</div>
                  <div className="text-xs font-bold text-white truncate">{user?.username || user?.email}</div>
                  <div className={`text-[10px] font-mono mt-0.5 ${portalColor}`}>{user?.role?.toUpperCase()}</div>
                </div>
                <div className="p-1.5 space-y-1">
                  {user?.role === 'superadmin' && navigate && (
                    <button
                      onClick={() => { navigate(currentPath === '/superadmin' ? '/admin' : '/superadmin'); setUserMenuOpen(false); }}
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-indigo-300 hover:bg-indigo-500/10 text-[12px] font-semibold transition-all text-left"
                    >
                      <LayoutDashboard className="w-3.5 h-3.5 text-indigo-400" />
                      <span>{currentPath === '/superadmin' ? 'Switch to Admin' : 'Switch to Super Admin'}</span>
                    </button>
                  )}
                  <button
                    onClick={() => { logout(); setUserMenuOpen(false); }}
                    className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-rose-400 hover:bg-rose-500/10 text-[12px] font-semibold transition-all"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
